<script setup lang="ts">
import { ref, onMounted } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import MessageBox from "../../../../services/MessageBox";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import * as XLSX from "xlsx";
import { TableField } from "../../../../components/table/table";
import { formatNumber } from "../../../../utils/CommonFunctions";

const props = defineProps<{
  endPoint: string;
}>();

interface matchField {
  field_name: string;
  caption: string;
  requried: boolean;
  mapped?: string;
}

interface excelFields {
  id: string;
  name: string;
}

const {
  validateForm,
} = useFormValidation({
  price_lists_id: "",
  warehouses_id: "",
  currencys_id: "",
  exchange_rate: 1,
  outlets_id: "",
});
//setFieldRules("price_lists_id", [required("Select Price List")]);
//setFieldRules("warehouses_id", [required("Select Warehouse")]);
//setFieldRules("currencys_id", [required("Select Currency")]);
//setFieldRules("exchange_rate", [required("Select Exchange Rate")]);

const matchFields = ref<matchField[]>([
  { field_name: "date", caption: "Date", requried: true, mapped: "" },
  { field_name: "account_code", caption: "Account Code", requried: true, mapped: "" },
  {
    field_name: "currency",
    caption: "Currency Code",
    requried: true,
    mapped: "",
  },
  {
    field_name: "exchange_rate",
    caption: "Exchange Rate",
    requried: true,
    mapped: "",
  },
  {
    field_name: "reference",
    caption: "Reference",
    requried: false,
    mapped: "",
  },
  {
    field_name: "remarks",
    caption: "Remarks",
    requried: false,
    mapped: "",
  },
  { field_name: "dr", caption: "Debit Amount", requried: false, mapped: "" },
  { field_name: "cr", caption: "Credit Amount", requried: false, mapped: "" },
]);

const tableColumns = ref<TableField[]>([]);

const excelData = ref<any[][]>([]);
const colHeaders = ref<excelFields[]>([]);

const handleFileUpload = (files: any[]) => {
  console.log(files);
  const file = files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    if (e.target && e.target.result) {
      const data = new Uint8Array(e.target.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0]; // Get the first sheet name
      const sheet = workbook.Sheets[sheetName];
    let jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert sheet to JSON

    // Format date columns in the first row (headers)
    const dateColIndexes = (jsonData[0] as string[])
      .map((header: string, idx: number) =>
        header.toLowerCase().includes("date") ? idx : -1
      )
      .filter(idx => idx !== -1);

    // Format date values in each row (except header)
    jsonData = (jsonData as any[][]).map((row: any[], rowIdx: number) => {
      if (rowIdx === 0) return row;
      dateColIndexes.forEach(colIdx => {
        const val = row[colIdx];        
        if (val && !isNaN(Date.parse(val)) && typeof val !== "number") {
          // If already a valid date string, format it
          row[colIdx] = new Date(val).toISOString().slice(0, 10);
        } else if (typeof val === "number") {
          // Excel dates are numbers, convert using XLSX
          row[colIdx] = XLSX.SSF.format("yyyy-mm-dd", val);
        }
      });
      return row;
    });
    if (jsonData.length > 0) {
      const arr = (jsonData[0] as string[]).map(
        (text: string, index: number) => ({
        id: index.toString(),
        name: text,
        })
      );
      arr.forEach((element) => {
        colHeaders.value.push(element);
        tableColumns.value.push({
        name: element.name,
        caption: element.name,
        sortable: false,
        fieldType: element.name.toLowerCase().includes("date") ? "date" : "text",
        });
      });
      jsonData.splice(0, 1); // Remove the first row from data

      excelData.value = jsonData as any[][];
    }
    }
  };
  reader.readAsArrayBuffer(file);
};

const mappedHeaders = ref<matchField[]>([]);
interface MappedInvoiceRow {
    date: string,
    account_code:string,
    account_name:string,
    currency:string,
    chart_of_accounts_id: string,
    currencys_id: string,
    exchange_rate: number,
    remarks: string,
    dr: number,
    cr: number,
    valid: boolean;
    import: boolean;
    error: string;
}

const mappedData = ref<MappedInvoiceRow[]>([]);
const billsForImportData = ref<MappedInvoiceRow[]>([]);

const handlePreview = async () => {
  mappedHeaders.value = [];
  mappedData.value = [];
  dbData.value = [];
  const failed =
    matchFields.value.filter(
      (field) => field.mapped === "" && field.requried === true
    ).length > 0;
  if (failed) {
    MessageBox.show(
      "Please map all required fields before previewing the data.",
      "error"
    );
    return;
  }

    if (!validateForm()) {
    return;
  }

  const match_fields = matchFields.value.filter((field) => field.mapped !== "");
  match_fields.forEach((field) => {
    mappedHeaders.value.push(field);
  });
  console.log(mappedHeaders.value);
  excelData.value.forEach((element) => {
    let d = {
        date: matchFields.value.find(
        (field) => field.field_name === "date"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "date"
              )?.mapped
            )
          ]
        : "",
        account_code: matchFields.value.find(
        (field) => field.field_name === "account_code"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "account_code"
              )?.mapped
            )
          ]
        : "",
        currency: matchFields.value.find(
        (field) => field.field_name === "currency"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "currency"
              )?.mapped
            )
          ]
        : "",
        exchange_rate: matchFields.value.find(
        (field) => field.field_name === "exchange_rate"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "exchange_rate"
              )?.mapped
            )
          ]
        : 0,
        reference: matchFields.value.find(
        (field) => field.field_name === "reference"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "reference"
              )?.mapped
            )
          ]
        : "",
        remarks: matchFields.value.find(
        (field) => field.field_name === "remarks"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "remarks"
              )?.mapped
            )
          ]
        : "",
        dr: matchFields.value.find(
        (field) => field.field_name === "dr"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "dr"
              )?.mapped
            )
          ]
        : 0,
        cr: matchFields.value.find(
        (field) => field.field_name === "cr"
      )?.mapped
        ? element[
            Number(
              matchFields.value.find(
                (field) => field.field_name === "cr"
              )?.mapped
            )
          ]
        : 0,
        chart_of_accounts_id: "",
        account_name: "",
        currencys_id: "",
        valid: true,
        error: "",
        import: false
    };

    if ((d.dr > 0 || d.cr > 0 ) && d.date && d.account_code && d.currency && d.exchange_rate > 0) {
        mappedData.value.push(d);
    }
    
  });


const uniqueChartOfAccounts = Array.from(new Set(mappedData.value.map(d => d.account_code).filter(Boolean)));
const uniqueCurrencies = Array.from(new Set(mappedData.value.map(d => d.currency).filter(Boolean)));

// You can use these arrays as needed, e.g. log them:
console.log('Unique Product Codes:', uniqueChartOfAccounts);

const data = await getListData(uniqueChartOfAccounts, uniqueCurrencies);
console.log('Data from API:', data);
mappedData.value.forEach((record) => {
    console.log(record)
    const account = data.chartOfAccounts.find((v: any) => v.code.toLowerCase() === record.account_code?.toLowerCase());
    record.chart_of_accounts_id = account ? account.id : "";
    record.account_name = account ? account.name : "";
    const currency = data.currencys.find((c: any) => c.code.toLowerCase() === record.currency?.toLowerCase());
    record.currencys_id = currency ? currency.id : "";   
});


mappedData.value.forEach((record) => {

    billsForImportData.value.push({
        date: record.date,
        account_code:record.account_code,
        account_name: record.account_name,
        currency:record.currency,
        chart_of_accounts_id: record.chart_of_accounts_id,
        currencys_id: record.currencys_id,
        exchange_rate: record.exchange_rate,
        remarks: record.remarks,
        dr: record.dr,
        cr: record.cr,
        valid: record.valid,
        import: record.import,
        error: record.error,
    });
});

console.log('bills for import', billsForImportData.value);

  //handleGetDataFromDb();

  console.log(mappedData.value);
};

const getListData = async (chartOfAccounts: any, currencys: any) => {
// Call API and return the data
const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") + "data-for-import",
    {
        chartOfAccounts: chartOfAccounts,
        currencys: currencys,
    }
);
return response.data;
};

const getBgColor = (row: any) => {
  if (!row.date || row.date == "") {
    return "#ed8888";
  }
  if (!row.chart_of_accounts_id || row.chart_of_accounts_id == "") {
    return "#ed8888";
  }
  if (!row.currencys_id || row.currencys_id == "") {
    return "#ed8888";
  }
  if (!row.exchange_rate || row.exchange_rate <= 0) {
    return "#ed8888";
  }
  if ((!row.cr || row.cr <= 0) && (!row.dr || row.dr <=0) ) {
    return "#ed8888";
  }
  return "";
};

const validated = ref(false);
const handleValidate = () => {
    console.log(billsForImportData.value)
  let valid = true;
  billsForImportData.value.forEach((element, index) => {
    if (element.date == "") {
      valid = false;
      //return false;
    }
    if (element.currencys_id == "") {
      valid = false;
      //return false;
    }
    if (element.exchange_rate < 0) {
      valid = false;
      //return false;
    }
    if (element.chart_of_accounts_id == "") {
      valid = false;
      //return false;
    }
    if (element.dr <= 0 && element.cr <= 0) {
      valid = false;
      return false;
    }

    billsForImportData.value[index].valid = valid;
    if (index == billsForImportData.value.length - 1) {
      validated.value = valid;
    }
  });

const totalCredit = billsForImportData.value.reduce((sum, item) => {
  return Number(sum) + (Number(item.cr) || 0);
}, 0);

const totalDebit = billsForImportData.value.reduce((sum, item) => {
  return Number(sum) + (Number(item.dr) || 0);
}, 0);

if (totalCredit !== totalDebit) {
    valid = false;
    MessageBox.show(
      `Total Debit (${formatNumber(totalCredit)}) does not equal Total Credit (${formatNumber(totalCredit)}). Please correct the amounts.`,
      "error"
    );
  }

  console.log("Validated", validated.value);
    if (!valid) {
        MessageBox.show(
        "Please fix all invalid records before validating.",
        "error"
        );
    } else {
        MessageBox.show("All records are valid.", "info");
    }
};


const handleImportPost = async () => {
    let param = {
        date: billsForImportData.value[importIndex.value].date,
        currencys_id: billsForImportData.value[importIndex.value].currencys_id,
        exchange_rate: billsForImportData.value[importIndex.value].exchange_rate,
        ledgers: billsForImportData.value,
    }
  await api.post(
        (props.endPoint.slice(-1) == "/"
          ? props.endPoint
          : props.endPoint + "/") + "create",
          param,  {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      hideError: true
    }
      ).then((response) => {
        if (response.success == true) {
          Toast.show("All records imported successfully", "info");
        } else {
          Toast.show(
            "Error importing record Ledgers",
            "error"
          );
        }
      })
      .catch((error => {
        console.log(error);
         billsForImportData.value[importIndex.value].import = false;
         billsForImportData.value[importIndex.value].error = error.response.message || "Error importing record";
        Toast.show(
          "Error importing record " +
            (importIndex.value + 1) +
            " of " +
            billsForImportData.value.length +
            " records",
          "error"
        );
      }));
};

const importIndex = ref(0);
const handleImport = async () => {
  if (dbData.value.filter((record) => getBgColor(record) != "").length > 0) {
    MessageBox.show(
      "Please fix all invalid records before importing.",
      "error"
    );
    return;
  } else {
  console.log('start posting', dbData.value[0]);
  importIndex.value = 0;
  handleImportPost()
  }
};

interface DbRow {
  date: string,
    account_code:string,
    account_name: string,
    currency:string,
    chart_of_accounts_id: string,
    currencys_id: string,
    exchange_rate: number,
    remarks: string,
    dr: number,
    cr: number,
    valid: boolean;
    import: boolean;
    error: string;
  [key: string]: any; // Add this to allow additional properties
}

const dbData = ref<DbRow[]>([]);

onMounted(() => {});


</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        title="Import using Excel Files"
        end-point=""
        :readonly="true"
        :show-drop-down="false"
        @close="$emit('close')"
        :permissions="[]"
        :show-share-icons="false"
        :show-save-icon="false"
      >
        <template>
          <Buttons caption="Import" icon="Import" @click="$emit('import')" />
        </template>
      </DocumentHeader>
    </template>

    <template #form>
      <div class="grid grid-cols-5 gap-6 mb-8">
        <div>
          <FileUpload
            label="Excel File"
            accept=".xlsx, .xls"
            @upload-success="handleFileUpload"
            path=""
            :local="true"
          />
          <div class="grid grid-cols-2 gap-2 mt-2">            

            <Buttons
              caption="Prepare Data"
              icon="ListChecks"
              @click="handlePreview"
              className="mt-2"
            />
            <Buttons
              caption="Validate"
              icon="ShieldCheck"
              v-if="billsForImportData.length > 0"
              @click="handleValidate"
              className="mt-2"
            />

            <Buttons
              caption="Import"
              icon="Import"
              class="col-span-2"
              type="info"
              v-if="validated"
              @click="handleImport"
              className="mt-2"
            />
          </div>
        </div>
        <div class="col-span-4 grid grid-cols-5 gap-1">
          <div v-for="(field, index) in matchFields" :key="index">
            <SearchSelect
              :label="field.caption"
              :options="colHeaders"
              :multiple="false"
              :placeholder="'Select Excel Fields'"
              v-model="field.mapped"
            />
          </div>
        </div>
      </div>
      <div v-if=" billsForImportData.length > 0">
        Total {{ billsForImportData.length }} records,
        <span
          >{{
            billsForImportData.filter((record) => getBgColor(record) != "").length
          }}
          Invalid Records</span
        >
        <table
          class="divide-y divide-gray-200 dark:divide-gray-700"
          id="table-records"
        >
          <thead>
            <tr id="table-headers">
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Chart Of Account
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Currency
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Exch. Rate
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Debit
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Credit
              </th>              
              <!--<th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Delivery Address
              </th>-->
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
          >
            <template v-for="(row, index) in billsForImportData" :key="index">
              <tr
                class="hover:bg-gray dark:hover:bg-gray-700"
                :style="'background-color: ' + getBgColor(row)"
              >
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                >
                <div class="flex items-center">
                  {{ row.date }}
                  </div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <span >{{ row.account_code }} - {{ row.account_name }}</span>
                  <SearchSelect
                    v-if="row.chart_of_accounts_id === ''"
                    label=""
                    :remote="true"
                    palceholder="Select Account"
                    end-point="finance/accounting/chart-of-accounts"
                    v-model="row.chart_of_accounts_id"
                  />
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <span v-if="row.currency">{{ row.currency }}</span>
                  <SearchSelect
                    v-else
                    label=""
                    palceholder="Select Currency"
                    end-point="finance/master-data/currency"
                    v-model="row.currencys_id"
                  />
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                >
                  {{ row.exchange_rate }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">
                   {{ formatNumber(row.dr) }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 dark:text-white text-right">
                   {{ formatNumber(row.cr) }}
                </td>
              </tr>
              <tr v-if="row.error" class="bg-red-200 dark:bg-red-900">
                <td
                  colspan="11"
                  class="px-6 py-4 text-sm text-white9400 dark:text-white"
                >
                {{ row.error }}
                </td>
                </tr>
            </template>
          </tbody>
          <thead>
            <tr id="table-headers">
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right col-span-4" colspan="4"
              >
                Total
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {{formatNumber(billsForImportData.reduce((sum, item) => Number(sum) + (Number(item.dr) || 0), 0))}}
              </th>
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {{formatNumber(billsForImportData.reduce((sum, item) => Number(sum) + (Number(item.cr) || 0), 0))}}
              </th>
            </tr>
          </thead>
        </table>
      </div>
      <div v-else>
        <table
          class="divide-y divide-gray-200 dark:divide-gray-700"
          id="table-records"
        >
          <thead>
            <tr id="table-headers">
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                v-for="h in colHeaders"
              >
                {{ h.name }}
              </th>
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
          >
            <template v-for="(row, eIndex) in excelData" :key="eIndex">
              <tr class="hover:bg-gray dark:hover:bg-gray-700" :id="'row-' + eIndex">
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                  v-for="(cell, cellIndex) in row"
                  :key="cellIndex"
                >
                  {{ cell }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :readonly="true"
        end-point="endPoint"
        :permissions="[]"
        title="Import using Excel Files"
        @close="$emit('close')"
        :show-new-icon="false"
        :show-share-icons="false"
        :show-save-icon="false"
      />
    </template>
  </ModalFormComponent>
</template>
